import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, input, signal, Signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  Document,
  Incident,
  IncidentCurrentState,
  INCIDENT_TABLE_ABRIDGED_COLUMNS,
  INCIDENT_TABLE_COLUMNS,
  INCIDENT_TIMELINE_STATES,
  IncidentTableColumn,
} from '../../models/incident';
import { AppModule } from '../../app.module';
import { AuthService } from '../../auth.service';
import { IncidentService } from '../../shared/incident.service';
import { NotificationService } from '../../shared/notification.service';
import { ReportService } from '../../shared/report.service';
import { Router } from '@angular/router';
import { Base64Report } from '../../models/report';
import { User as AuthUser } from '../../models/user';
import { Globals } from '../../shared/functions';
import { FileUploader } from '../../shared/file-uploader/file-uploader';
import { QuoteService } from '../../shared/quote.service';
import { MapTrackSpTech } from "../../map/map-track-sp-tech/map-track-sp-tech";
import { IncidentCloseModal } from '../incident-close-modal/incident-close-modal';
import { IncidentSearch } from '../incident-search/incident-search';
import { Subscription } from 'rxjs';
import { ServiceProviderService } from '../../service-provider/service-provider.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { WaGroupOpener } from '../../shared/wa-group-opener/wa-group-opener';
import { WhatsappService } from '../../whatsapp/whatsapp.service';
import { IncidentClientLink } from '../../models/client';
import { SharedDataService } from '../../shared/shareddata.service';
import { UserType } from '../../shared/enums';
import { ChatService } from '../../shared/chat.service';
import { ChatSenderType } from '../../models/chat';
import { provideNzIconsPatch } from 'ng-zorro-antd/icon';
import { ExclamationCircleOutline } from '@ant-design/icons-angular/icons';

type OpenIncidentTableRow =
  | {
      kind: 'group';
      key: string;
      name: string;
      incidentCount: number;
      isWhatsappOnlyNotificationRecipient: boolean;
    }
  | {
      kind: 'incident';
      key: string;
      incident: Incident;
    };

interface OpenIncidentGroup {
  key: string;
  name: string;
  incidentCount: number;
  isWhatsappOnlyNotificationRecipient: boolean;
  sortOrder: number;
  incidents: Incident[];
}

@Component({
  selector: 'app-open-incident-table',
  providers: [provideNzIconsPatch([ExclamationCircleOutline])],
  imports: [AppModule, FileUploader, MapTrackSpTech, IncidentCloseModal, IncidentSearch, WaGroupOpener],
  templateUrl: './open-incident-table.html',
  styleUrl: './open-incident-table.less',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpenIncidentTable {

  currentUser = signal<AuthUser | null>(null);
  public incidents = computed(() => this.incidentService.openIncidents());
  public isLoading = computed(() => this.incidentService.isLoadingOpen$());
  isAbridged = input<boolean>(false);
  etaChanges = input<Map<number, number>>(new Map());
  selectedIncident = input<Incident | null>(null);
  
  isTrackTechModalVisible = false;
  public selectedIncidentId = signal<number>(0);
  public selectedUploadDocumentClassificationId = signal<number>(1);

  public isUploadModalVisible = false;
  public isCloseIncidentModalVisible = false;
  public selectedIncidentIdToClose: number = 0;

  public isCompleteWithoutWorkReportConfirmModalVisible: boolean = false;

  public isWaGroupOpenerModalVisible = false;
  public waGroupOpenerIncident = signal<Incident | null>(null);
  public waGroupOpenerLink = '';
  public waGroupOpenerIsFleet = false;

  public columns = computed<IncidentTableColumn[]>(() => {
    const user = this.authService.user;
    const userClientTypes = user?.clientTypes || [];

    return (
      this.isAbridged()
        ? INCIDENT_TABLE_ABRIDGED_COLUMNS : INCIDENT_TABLE_COLUMNS
    )
      .filter((config) =>
        userClientTypes.some((clientType) =>
          config.visibleFor.includes(clientType)
        )
      )
      .map((config) => ({
        key: config.key,
        title: config.title,
        width: config.width,
      }));
  });

  public statusCounts = computed(() => {
    const counts = new Map<string, number>();
    this.incidents().forEach(incident => {
      const status = incident.status || 'Unknown';
      counts.set(status, (counts.get(status) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([status, count]) => ({ status, count }));
  });

  public gFuncs = Globals.Functions;

  constructor(
    public incidentService: IncidentService,
    public notification: NotificationService,
    public reportService: ReportService,
    public authService: AuthService,
    private router: Router,
    private quoteService: QuoteService,
    private cdr: ChangeDetectorRef,
    private spService: ServiceProviderService,
    private whatsappService: WhatsappService,
    private sharedData: SharedDataService,
    private chatService: ChatService
  ) {
    effect(() => {
      if (!this.authService.isClientCallCentre) return;
      for (const incident of this.incidents()) {
        void this.chatService.loadReadState(incident.incidentId, ChatSenderType.Callcentre);
        this.chatService.trackEscalations(incident.chatEscalations || []);
      }
    });
    setInterval(() => {
      this.cdr.markForCheck();
    }, 60000);
  }

  public searchTerm = signal<string>('');
  public dateRange = signal<[Date, Date] | null>(null);
  public selectedCallcentreOperatorIds = signal<Set<number>>(new Set());
  public displayAsGroup = signal<boolean>(false);
  private metadataVersion = signal<number>(0);

  public get callcentreOperators() {
    const operatorIdsWithOpenIncidents = new Set(
      this.incidentsBeforeCallcentreOperatorFilter().flatMap((incident) =>
        Array.from(this.getIncidentCallcentreOperatorIds(incident))
      )
    );

    return this.sharedData.callcentreOperators
      .filter((operator) => operatorIdsWithOpenIncidents.has(operator.userId))
      .sort((left, right) =>
        this.getCallcentreOperatorName(left).localeCompare(this.getCallcentreOperatorName(right))
      );
  }

  public incidentsBeforeCallcentreOperatorFilter = computed(() => {
    let filtered = this.incidents();

    const searchTerm = this.searchTerm().toLowerCase();
    if (searchTerm && searchTerm.trim() !== '') {
      filtered = filtered.filter(incident => {
        const locationMatch = incident.location?.description?.toLowerCase().includes(searchTerm) || false;
        const driverMatch = incident.driver?.name?.toLowerCase().includes(searchTerm) || false;
        const vehicleMatch = incident.fleetVehicle?.registrationNumber?.toLowerCase().includes(searchTerm) || false;
        const statusMatch = incident.status?.toLowerCase().includes(searchTerm) || false;
        const incidentNumberMatch = incident.incidentNumber?.toLowerCase().includes(searchTerm) || false;
        const clientNameMatch = incident.clientName?.toLowerCase().includes(searchTerm) || false;
        const orderNumberMatch = incident.orderNumber?.toLowerCase().includes(searchTerm) || false;

        return locationMatch || driverMatch || vehicleMatch || statusMatch || incidentNumberMatch || clientNameMatch || orderNumberMatch;
      });
    }
    return filtered;
  });

  public filteredIncidents = computed(() => {
    const selectedCallcentreOperatorIds = this.selectedCallcentreOperatorIds();

    if (selectedCallcentreOperatorIds.size === 0) {
      return this.incidentsBeforeCallcentreOperatorFilter();
    }

    return this.incidentsBeforeCallcentreOperatorFilter().filter((incident) =>
      Array.from(this.getIncidentCallcentreOperatorIds(incident)).some((operatorId) =>
        selectedCallcentreOperatorIds.has(operatorId)
      )
    );
  });

  public tableRows = computed<OpenIncidentTableRow[]>(() => {
    this.metadataVersion();
    const incidents = this.filteredIncidents();

    if (!this.displayAsGroup()) {
      return incidents.map((incident) => ({
        kind: 'incident' as const,
        key: `incident-${incident.incidentId}`,
        incident
      }));
    }

    const callcentreOperatorsById = new Map(
      this.sharedData.callcentreOperators.map((operator) => [operator.userId, operator])
    );
    const groups = new Map<string, OpenIncidentGroup>();

    for (const incident of incidents) {
      const operatorIds = this.getIncidentCallcentreOperatorIds(incident);

      if (operatorIds.size === 0) {
        this.addIncidentToOperatorGroup(groups, 'unassigned', 'Unassigned', 2, false, incident);
        continue;
      }

      for (const operatorId of operatorIds) {
        const metadataOperator = callcentreOperatorsById.get(operatorId);
        const incidentOperator = incident.incidentOperators?.find(
          (operator) => operator.operatorId === operatorId && operator.userType === UserType.callcenterOperator
        );
        const name = metadataOperator
          ? this.getCallcentreOperatorName(metadataOperator)
          : incidentOperator?.name
            ? `Unknown / inactive: ${incidentOperator.name}`
            : `Unknown / inactive operator #${operatorId}`;

        this.addIncidentToOperatorGroup(
          groups,
          `operator-${operatorId}`,
          name,
          metadataOperator ? 0 : 1,
          metadataOperator?.isWhatsappOnlyNotificationRecipient ?? false,
          incident
        );
      }
    }

    return Array.from(groups.values())
      .sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name))
      .flatMap((group) => [
        {
          kind: 'group' as const,
          key: `group-${group.key}`,
          name: group.name,
          incidentCount: group.incidentCount,
          isWhatsappOnlyNotificationRecipient: group.isWhatsappOnlyNotificationRecipient
        },
        ...group.incidents.map((incident) => ({
          kind: 'incident' as const,
          key: `${group.key}-incident-${incident.incidentId}`,
          incident
        }))
      ]);
  });
  
  private etaUpdateSubscription: Subscription;
  private incidentUpdateSubscription: Subscription;
  private metadataLoadedSubscription: Subscription;
  
  ngOnInit() {
    this.currentUser.set(this.authService.user || null);
    this.incidentService.loadOpenIncidents().subscribe();
    this.setupEtaUpdates();
    this.setupIncidentUpdates();
    this.metadataLoadedSubscription = this.sharedData.metadataLoaded.subscribe(() => {
      this.metadataVersion.update((version) => version + 1);
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy() {
    this.etaUpdateSubscription?.unsubscribe();
    this.incidentUpdateSubscription?.unsubscribe();
    this.metadataLoadedSubscription?.unsubscribe();
  }

private setupEtaUpdates(): void {
  this.etaUpdateSubscription = this.incidentService.$etaUpdate.subscribe((etaUpdate) => {
    console.log(`ETA update received for incident ${etaUpdate.incidentId}:`, etaUpdate.newEta);
    this.cdr.markForCheck();
  });
}

private setupIncidentUpdates(): void {
  this.incidentUpdateSubscription = this.incidentService.$incidentUpdate.subscribe(() => {
    this.cdr.markForCheck();
  });
}

  public onClickViewIncident(incident: Incident) {
    this.router.navigate(['/incident', incident.incidentId]);
  }

  public unreadChatCount(incidentId: number): number {
    return this.authService.isClientCallCentre
      ? this.chatService.unreadCount(incidentId, ChatSenderType.Callcentre) : 0;
  }

  public hasChatEscalation(incidentId: number): boolean {
    return this.authService.isClientCallCentre && this.chatService.hasEscalation(incidentId);
  }

  public isCallcentreOperatorSelected(userId: number): boolean {
    return this.selectedCallcentreOperatorIds().has(userId);
  }

  public toggleCallcentreOperator(userId: number): void {
    const selectedOperatorIds = new Set(this.selectedCallcentreOperatorIds());
    if (selectedOperatorIds.has(userId)) {
      selectedOperatorIds.delete(userId);
    } else {
      selectedOperatorIds.add(userId);
    }
    this.selectedCallcentreOperatorIds.set(selectedOperatorIds);
  }

  public clearCallcentreOperatorFilter(): void {
    this.selectedCallcentreOperatorIds.set(new Set());
  }

  public setDisplayAsGroup(displayAsGroup: boolean): void {
    this.displayAsGroup.set(displayAsGroup);
  }

  public getCallcentreOperatorName(operator: { firstName?: string; lastName?: string; userId: number }): string {
    const firstName = operator.firstName?.trim();
    const lastName = operator.lastName?.trim();

    if (!firstName) {
      return lastName || `User #${operator.userId}`;
    }

    const firstNameOccurrences = this.sharedData.callcentreOperators.filter(
      (callcentreOperator) => callcentreOperator.firstName?.trim().toLocaleLowerCase() === firstName.toLocaleLowerCase()
    ).length;

    return firstNameOccurrences > 1 && lastName ? `${firstName} ${lastName}` : firstName;
  }

  private getIncidentCallcentreOperatorIds(incident: Incident): Set<number> {
    const operatorIds = new Set<number>();

    if (incident.callcentreOperatorId && incident.callcentreOperatorId > 0) {
      operatorIds.add(incident.callcentreOperatorId);
    }

    for (const operator of incident.incidentOperators ?? []) {
      if (operator.userType === UserType.callcenterOperator && operator.operatorId > 0) {
        operatorIds.add(operator.operatorId);
      }
    }

    return operatorIds;
  }

  private addIncidentToOperatorGroup(
    groups: Map<string, OpenIncidentGroup>,
    key: string,
    name: string,
    sortOrder: number,
    isWhatsappOnlyNotificationRecipient: boolean,
    incident: Incident
  ): void {
    let group = groups.get(key);

    if (!group) {
      group = { key, name, incidentCount: 0, isWhatsappOnlyNotificationRecipient, sortOrder, incidents: [] };
      groups.set(key, group);
    }

    group.incidents.push(incident);
    group.incidentCount++;
  }

  public onClickNavigate(incident: Incident) {
    this.router.navigate(['/incident/navigate', incident.incidentId]);
  }

  isLoadingProofOfWork: boolean = false;

  public onClickProofOfWork(incidentId: number) {
    this.isLoadingProofOfWork = true;
    this.reportService.generateProofOfWorkReport(incidentId).subscribe({
      next: (report: Base64Report) => {
        this.notification.showSuccess(
          'Proof of Work report generated successfully.'
        );
        this.downloadFile(report);
        this.isLoadingProofOfWork = false;
      },
      error: () => {
        this.notification.showError('Failed to generate Proof of Work report.');
        this.isLoadingProofOfWork = false;
      },
    });
  }

  private downloadFile(report: Base64Report) {
    const link = document.createElement('a');
    link.href = 'data:' + report.fileType + ';base64,' + report.base64Data;
    link.download = report.fileName;
    link.click();
  }

  // public getActiveQuotes(incidentClients: any[]): any[] {
  //   return incidentClients.filter((ic) => ic.activeQuoteId !== null);
  // }

  public getActiveServiceProviderLink(incident: Incident): IncidentClientLink | undefined {
    return incident.incidentClients.find(
      (link) => link.connectionTypeId === 3 && link.isActive
    );
  }

  public getServiceProviderName(incident: Incident): string {
    const spLink = this.getActiveServiceProviderLink(incident);
    return spLink ? spLink.clientName : '';
  }

  public getServiceProviderWAGroupLink(incident: Incident): string {
    const spLink = this.getActiveServiceProviderLink(incident);
    return spLink ? spLink.whatsappGroupLink || '' : '';
  }

  public isSpDirectlyLinked(clientId: number): boolean {
    const spLink = this.sharedData.serviceProviderLinks.find(sp => sp.clientId === clientId);
    if (!spLink) return false;
    return spLink.callcentreId === null || spLink.callcentreId === this.authService.user?.clientId;
  }

  public getSpCallcentreName(clientId: number): string | null {
    const spLink = this.sharedData.serviceProviderLinks.find(sp => sp.clientId === clientId);
    return spLink ? spLink.callcentreName : null;
  }

  public getViaServiceProviderDisplayName(spLink: IncidentClientLink): string {
    const callcentreName = this.getSpCallcentreName(spLink.clientId);
    return callcentreName ? `${callcentreName}'s Service Provider` : spLink.clientName || 'Service Provider';
  }

  public getServiceProviderWAGroupName(incident: Incident): string {
    const spLink = this.getActiveServiceProviderLink(incident);
    return spLink ? spLink.whatsappGroupName || '' : '';
  }

  public getSPId(incident: Incident): number {
    const spLink = this.getActiveServiceProviderLink(incident);
    return spLink ? spLink.clientId : 0;
  }


  // public getServiceProviderDriverName(incident: Incident): string {
  //   const spLink = incident.incidentOperators.find(
  //     (link) => link.userType === 5 && link.isActive
  //   );
  //   return spLink ? spLink.name : '';
  // }

  // public getFleetName(incident: Incident): string {
  //   return incident && incident.clientName ? incident.clientName : 'Unknown';
  // }

  // public getCallCentreName(incident: Incident): string {
  //   const cc = incident.incidentOperators.find((io) => io.isActive === null); //Need to confirm condition
  //   return cc ? cc.name : 'Unknown';
  // }

  // public calculateEstimatedArrival(incident: Incident): string {
  //   const dtLogged = incident.dtLogged;
  //   const sp = incident.incidentClients.find(ic => ic.isActive === true && ic.incidentTravelTimeMinutes > 0); //confirm how to identify service provider with status/isActive/quote etc
  //   const incidentTravelTimeMinutes = sp ? sp.incidentTravelTimeMinutes : 0;

  //   if (!dtLogged || !incidentTravelTimeMinutes) return 'N/A';

  //   const loggedDate = new Date(dtLogged + 'Z');
  //   const arrivalTime = new Date(loggedDate.getTime()  + incidentTravelTimeMinutes * 60000);
  //   return arrivalTime.toLocaleTimeString() + ' (' + incidentTravelTimeMinutes + ' mins)';
  // }

  public getIncidentStatusPercentage(statusId: number): number {
    return (statusId / 11) * 100;
  }

  /**
   * Derives the current "awaiting" state for an incident by walking the INCIDENT_TIMELINE_STATES
   * CONST backwards and finding the last completed step without the next one being done.
   * Returns null when the incident is completed (dtCompleted is set).
   */
  public getIncidentCurrentState(incident: Incident): IncidentCurrentState | null {
    // Completed — no "awaiting" state
    if (incident.incidentActions?.dtCompleted) {
      return null;
    }

    const now = new Date().getTime();

    // Find the step whose completedFields contain the single most recent timestamp across all steps.
    // This means the newest action always determines the current state, regardless of ordering.
    let latestStepIndex = -1;
    let latestTime = -Infinity;
    let latestRawDate: any = null;

    for (let i = 0; i < INCIDENT_TIMELINE_STATES.length; i++) {
      const step = INCIDENT_TIMELINE_STATES[i];
      const source = step.refSource === 'incident'
        ? incident
        : incident.incidentActions;

      if (!source) continue;

      for (const field of step.completedFields) {
        const raw = (source as any)[field];
        if (!raw) continue;
        const t = new Date(raw + 'Z').getTime();
        if (t > latestTime) {
          latestTime = t;
          latestStepIndex = i;
          latestRawDate = raw;
        }
      }
    }

    if (latestStepIndex === -1) return null;

    const step = INCIDENT_TIMELINE_STATES[latestStepIndex];
    const referenceTime = new Date(latestRawDate + 'Z');
    const minutesElapsed = Math.floor((now - referenceTime.getTime()) / 60000);

    // ETA-based severity: compute from breakdownTechnicianDriverEta instead of the step's reference date
    if (step.etaBasedThresholds) {
      const eta = incident.breakdownTechnicianDriverEta;
      if (!eta) {
        // No ETA set yet — normal severity, no countdown
        return {
          awaitingLabel: step.awaitingLabel,
          referenceTime,
          minutesElapsed,
          severity: 'normal',
        };
      }
      const etaTime = new Date(eta + 'Z');
      // Positive = minutes remaining; negative = minutes overdue
      const etaMinutesRemaining = Math.floor((etaTime.getTime() - now) / 60000);
      const minutesOverdue = -etaMinutesRemaining;
      const { overdueWarnMinutes, overdueCriticalMinutes } = step.etaBasedThresholds;
      const severity: 'normal' | 'warn' | 'critical' =
        minutesOverdue >= overdueCriticalMinutes ? 'critical'
        : minutesOverdue >= overdueWarnMinutes ? 'warn'
        : 'normal';
      return {
        awaitingLabel: step.awaitingLabel,
        referenceTime,
        minutesElapsed,
        severity,
        etaMinutesRemaining,
      };
    }

    const severity: 'normal' | 'warn' | 'critical' =
      minutesElapsed >= step.criticalMinutes ? 'critical'
      : minutesElapsed >= step.warnMinutes ? 'warn'
      : 'normal';

    return {
      awaitingLabel: step.awaitingLabel,
      referenceTime,
      minutesElapsed,
      severity,
    };
  }

  public formatStatus(incident: Incident): string {
    const days = this.getIncidentStatusPercentage(incident.statusId);
    return `${days} Days`;
  }

  // public viewDoc(incident: Incident, docTypeId: number): void {
  //   const docUrl = incident.documents.find(
  //     (doc) => doc.documentClassificationId === docTypeId
  //   )?.url;
  //   if (docUrl) {
  //     window.open(docUrl, '_blank');
  //   } else {
  //     this.notification.showError('Document not available.');
  //   }
  // }

  public isTechLate(incident: Incident): boolean {
    if (incident.statusId === 7) { // 7 = Technician en route
    return incident.originalEta < incident.breakdownTechnicianDriverEta;
  }
  else{
    return new Date(incident.originalEta + 'Z') < new Date();
  }
}

  public calculateTimeElapsed(dtLogged: Date): string {
    const loggedTime = new Date(dtLogged + 'Z').getTime();
    const currentTime = new Date().getTime();
    const elapsedMinutes = (currentTime - loggedTime) / (1000 * 60);
    return `${Math.floor(elapsedMinutes)} mins`;
  }

  public calculateEstimatedArrival(incident: Incident): string {
    if (incident.breakdownTechnicianDriverEta) {
      const eta = new Date(incident.breakdownTechnicianDriverEta + 'Z');
      const travelTime = this.gFuncs.calculateMinutes(
        incident.breakdownTechnicianDriverEta
      );

      return `${eta.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      })} (${travelTime} mins)`;
    }
    return 'Not Assigned';
  }

  public getTravelTime(incident: Incident): number | null {
    if (incident.breakdownTechnicianDriverEta) {
      return this.gFuncs.calculateMinutes(
        incident.breakdownTechnicianDriverEta
      );
    }
    return null;
  }

  public isBDOverTime(incident: Incident): boolean {
    return false;
  }

  public onClickUploadPoW(incidentId: number) {
    this.selectedIncidentId.set(incidentId);
    this.selectedUploadDocumentClassificationId.set(1); // Proof of Work
    this.isUploadModalVisible = true;
  }

  public onClickUploadSPInvoice(incidentId: number) {
    this.selectedIncidentId.set(incidentId);
    this.selectedUploadDocumentClassificationId.set(4); // Service Provider Invoice
    this.isUploadModalVisible = true;
  }

  public onClickUploadFleetInvoice(incidentId: number) {
    this.selectedIncidentId.set(incidentId);
    this.selectedUploadDocumentClassificationId.set(6); // Fleet Invoice
    this.isUploadModalVisible = true;
  }

  public hasSPInvoice(incident: Incident): boolean {
    //Property in Incident class is not updating, need to convert to signal to work
    return incident.documents.some(
      (doc) => doc.documentClassificationId === 4
    ); // 4 = Invoice external
  }

  public hasFleetInvoice(incident: Incident): boolean {
    return incident.documents.some(
      (doc) => doc.documentClassificationId === 6
    ); // 6 = Fleet Invoice
  }

 
  public onUploadComplete(event: Document[]): void {
    // Add documents to incident, with duplication checking
    const incident = this.incidents().find(
      (inc) => inc.incidentId === this.selectedIncidentId()
    );
    if (incident) {
      event.forEach((newDoc) => {
        const exists = incident.documents.some(
          (doc) => doc.documentId === newDoc.documentId
        );
        if (!exists) {
          incident.documents.push(newDoc);
          console.log("added new doc to incdeint", newDoc);
          // Update SP Invoce status to 12 when SP invoice is uploaded
          if (newDoc.documentClassificationId === 4) {
            incident.statusId = 12;
            this.notification.showSuccess('Service Provider invoice uploaded.');
          }
          // Update incident status to 17 when fleet invoice is uploaded
          if (newDoc.documentClassificationId === 6) {
            incident.statusId = 17;
            this.notification.showSuccess('Fleet invoice uploaded.');
          }
        }
      });
    }
    this.isUploadModalVisible = false;
  }

  public onClickViewSPInvoice(incidentId: number) {
    const incident = this.incidents().find(
      (inc) => inc.incidentId === incidentId
    );
    if (incident) {
      const invoiceDoc = incident.documents.find(
        (doc) => doc.documentClassificationId === 4
      );
      if (invoiceDoc && invoiceDoc.url) {
        window.open(invoiceDoc.url, '_blank');
      } else {
        this.notification.showError('Service Provider Invoice not available.');
      }
    }
  }

  public onClickViewFleetInvoice(incidentId: number) {
    const incident = this.incidents().find(
      (inc) => inc.incidentId === incidentId
    );
    if (incident) {
      const invoiceDoc = incident.documents.find(
        (doc) => doc.documentClassificationId === 6
      );
      if (invoiceDoc && invoiceDoc.url) {
        window.open(invoiceDoc.url, '_blank');
      } else {
        this.notification.showError('Fleet Invoice not available.');
      }
    }
  }

  public onClickGoToServiceProviderQuote(incidentId: number) {
    const quoteId = this.incidents().find(inc => inc.incidentId === incidentId)?.incidentClients.find(link => link.connectionTypeId === 3 && link.isActive === true)?.activeQuoteId;
    if (quoteId && quoteId > 0)
      this.router.navigate(['/quote', quoteId]);
    else
      this.notification.showWarning("No active quote found for the selected service provider.");
  }

  public getServiceProviderQuoteTotal(incidentId: number): number {
    let quoteSummary = this.incidents().find(inc => inc.incidentId === incidentId)?.incidentClients.find(link => link.connectionTypeId === 3 && link.isActive === true)?.quoteSummary;
    if (quoteSummary && quoteSummary.quoteOptions.length > 0) {
      return quoteSummary.quoteOptions[0].total;
    }
    else {
      return 0;
    }
  }

  public getFleetQuoteTotal(incidentId: number): number {
    let quoteSummary = this.incidents().find(inc => inc.incidentId === incidentId)?.incidentClients.find(link => link.connectionTypeId === 2 && link.isActive === true)?.quoteSummary;
    if (quoteSummary && quoteSummary.quoteOptions.length > 0) {
      return quoteSummary.quoteOptions[0].total;
    }
    else {
      return 0;
    }
  }

  public onClickReInvoiceCustomer(incidentId: number) {
    const quoteId = this.incidents().find(inc => inc.incidentId === incidentId)?.incidentClients.find(link => link.connectionTypeId === 3 && link.isActive === true)?.activeQuoteId;
    const incident = this.incidents().find(inc => inc.incidentId === incidentId);
    if (quoteId && quoteId > 0 && incident) {
      this.quoteService.generateQuoteToFleet(quoteId, incidentId, incident?.clientId, this.authService.user!.clientId).subscribe({
        next: (response) => {
          this.notification.showSuccess('Quote generated for fleet');
          incident.statusId = 16; //set incident status to fleet invoiced
          this.router.navigate(['/quote', response.quoteId]);
        },
        error: (error) => {
          this.notification.handleError(error, "Failed to generate quote for fleet.");
        }
      });
    }
  }

  public onClickCompleteIncident(incidentId: number) {
    this.selectedIncidentIdToClose = incidentId;
    this.isCloseIncidentModalVisible = true;
  }

  public onIncidentClosed() {
    // Refresh the incident in the list
    const incident = this.incidents().find(
      (inc) => inc.incidentId === this.selectedIncidentIdToClose
    );
    if (incident) {
      // Optionally update the status or trigger a refresh from parent
      this.notification.showSuccess('Breakdown closed successfully.');
    }
    this.isCloseIncidentModalVisible = false;
    this.cdr.markForCheck();
  }

  /**
   * Gets the background color for action columns based on incident status
   * @param columnKey - The key of the column (pow, spinvoice, updatepo, reinvoice, invoice)
   * @param incident - The incident data
   * @returns Background color: '#d4edda' (green) when done, '#f8d7da' (red) when current action, '' (none) when not applicable
   * This will need to be incorporated into Nic's existing column layout logic in the incident class
   */
  public getActionColumnBackground(columnKey: string, incident: Incident): string {
    const statusId = incident.statusId;
    
    switch (columnKey) {
      case 'pow':
        if (statusId >= 11) return '#d4edda'; // green
        if (statusId >= 5 && statusId < 11) return '#f8d7da'; // red
        return ''; // no color
      
      case 'spinvoice':
        if (statusId >= 12) return '#d4edda'; // green
        if (statusId >= 11 && statusId < 12) return '#f8d7da'; // red
        return ''; // no color
      
      case 'updatepo':
        if (statusId > 12) return '#d4edda'; // green
        if (statusId === 12) return '#f8d7da'; // red
        return ''; // no color
      
      case 'reinvoice':
        if (statusId > 15) return '#d4edda'; // green
        if (statusId === 15) return '#f8d7da'; // red
        return ''; // no color
      
      case 'invoice':
        if (statusId > 16) return '#d4edda'; // green
        if (statusId === 16) return '#f8d7da'; // red
        return ''; // no color
      
      default:
        return ''; // no color for other columns
    }
  }

  public trackTechForIncident = signal<Incident | null>(null);

  public openTrackDriverModal(incident: Incident): void {
    this.trackTechForIncident.set(incident);
    this.isTrackTechModalVisible = true;
  }

  public openFleetTrackingLink(incidentId: number): void {
    window.open(`${environment.appUrl}report-new-breakdown/${incidentId}`, '_blank');
  }

  public openSPActionLink(incidentId: number): void {
    window.open(`${environment.appUrl}action/${incidentId}`, '_blank');
  }

  public openWaGroupOpener(incident: Incident, link: string, isFleet: boolean = false): void {
    this.waGroupOpenerIncident.set(incident);
    this.waGroupOpenerLink = link;
    this.waGroupOpenerIsFleet = isFleet;
    this.isWaGroupOpenerModalVisible = true;
  }

  public onSearchTermChange(searchTerm: string) {
    this.searchTerm.set(searchTerm);
  }

  public onDateRangeChange(dateRange: [Date, Date] | null) {
    this.dateRange.set(dateRange);
    if (dateRange && dateRange[0] && dateRange[1]) {
      // Load incidents for the selected date range
      this.incidentService.loadOpenIncidentsForDateRange(dateRange[0], dateRange[1]).subscribe({
        next: (incidents) => {
          this.notification.showSuccess(`Loaded breakdowns from ${dateRange[0].toLocaleDateString()} to ${dateRange[1].toLocaleDateString()}`);
        },
        error: (error) => {
          this.notification.showError('Failed to load breakdowns for selected date range', error.message);
        }
      });
    } else {
      // Load today's incidents when date range is cleared
      this.incidentService.loadOpenIncidents().subscribe({
        next: (incidents) => {
          this.notification.showSuccess(`Loaded today's breakdowns: ${incidents.length}`);
        },
        error: (error) => {
          this.notification.showError('Failed to load today\'s breakdowns', error.message);
        }
      });
    }
  }

  public onClearFilters() {
    this.searchTerm.set('');
    this.dateRange.set(null);
    
    this.incidentService.loadOpenIncidents().subscribe({
      next: (incidents) => {
        this.notification.showSuccess(`Loaded today's breakdowns after clearing filters: ${incidents.length}`);
      },
      error: (error) => {
this.notification.showError('Failed to load today\'s breakdowns', error.message);
      }
    });
  }

  public onClickDriverHasArrived(incident: Incident) {
    incident.statusId = 8; // Update status to "Technician Arrived"
    return this.incidentService.updateIncident(incident).subscribe({
      next: () => {
        this.notification.showSuccess('Breakdown status updated to Technician Arrived.');
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.notification.showError('Failed to update breakdown status.', error.message);
      }
    });
  }

  public isSPWAGroupModalOpen = signal<boolean>(false);
  public selectedSPId = signal<number>(0);
  public selectedIncidentIdForSPWAGroup = signal<number>(0);
  public isLoadingGroupName = signal<boolean>(false);

  waGroupForm = new FormGroup({
    whatsappGroupLink: new FormControl(''),
    whatsappGroupName: new FormControl('')
  });

  public openSPWAGroupModal(spId: number, incidentId: number): void {
    this.selectedSPId.set(spId);
    this.selectedIncidentIdForSPWAGroup.set(incidentId);
    this.waGroupForm.get('whatsappGroupLink')?.setValue('');
    this.waGroupForm.get('whatsappGroupName')?.setValue('');
    this.waGroupForm.get('whatsappGroupName')?.enable();
    this.isSPWAGroupModalOpen.set(true);
  }

  public onGroupLinkBlur(): void {
    const link = this.waGroupForm.get('whatsappGroupLink')?.value;
    if (!link) { return; }
    this.isLoadingGroupName.set(true);
    this.waGroupForm.get('whatsappGroupName')?.setValue('');
    this.waGroupForm.get('whatsappGroupName')?.disable();
    this.whatsappService.getGroupName(link).subscribe({
      next: (result) => {
        this.waGroupForm.get('whatsappGroupName')?.setValue(result.groupName || '');
        this.waGroupForm.get('whatsappGroupName')?.enable();
        this.isLoadingGroupName.set(false);
      },
      error: () => {
        this.waGroupForm.get('whatsappGroupName')?.enable();
        this.isLoadingGroupName.set(false);
      }
    });
  }

  public saveSPWAGroupLink(): void {
    const rawValues = this.waGroupForm.getRawValue();
    const linkToSave = rawValues.whatsappGroupLink;
    const groupNameToSave = rawValues.whatsappGroupName ?? '';

    console.log("Saving WhatsApp group link for SP ID:", this.selectedSPId(), "Link:", linkToSave);
    const spId = this.selectedSPId();
    if (spId && spId > 0) {
        this.spService.updateSPWAGroupLink(spId, linkToSave!, groupNameToSave).subscribe({
          next: () => {
          this.notification.showSuccess('WhatsApp group link updated successfully.');
          this.addWAGroupLinkToSP(this.selectedIncidentIdForSPWAGroup(), linkToSave!, groupNameToSave);
          this.isSPWAGroupModalOpen.set(false);
        },
        error: (error) => {
          this.notification.showError('Failed to update WhatsApp group link.', error.message);
        }
      });
    }
  }

  private addWAGroupLinkToSP(incidentId: number, whatsappGroupLink: string, whatsappGroupName: string): void {
    const incident = this.incidents().find(inc => inc.incidentId === incidentId);
    if (incident) {
      const spLink = incident.incidentClients.find(link => link.connectionTypeId === 3 && link.isActive);
      if (spLink) {
        spLink.whatsappGroupLink = whatsappGroupLink;
        spLink.whatsappGroupName = whatsappGroupName;
        this.cdr.markForCheck();
      }
    }
  }

  public incidentIdToCloseWithoutWorkReport: number = 0;

  public openCompleteWithoutWorkReportConfirmModal(incidentId: number): void {
    this.incidentIdToCloseWithoutWorkReport = incidentId;
    this.isCompleteWithoutWorkReportConfirmModalVisible = true;
  }

  public onClickCompleteWithoutWorkReport(): void {
    this.isCompleteWithoutWorkReportConfirmModalVisible = false;
        this.incidentService.completeWithoutWorkReport(this.incidentIdToCloseWithoutWorkReport)?.subscribe({
          next: (incident) => {
            this.notification.showSuccess('Breakdown completed without work report.');
            const index = this.incidentService.openIncidents().findIndex(inc => inc.incidentId === this.incidentIdToCloseWithoutWorkReport);
            if (index !== -1) {
              this.incidentService.openIncidents.update(incidents => {
                const updatedIncidents = [...incidents];
                updatedIncidents.splice(index, 1);
                return updatedIncidents;
              });
            }
          },
          error: (error) => {
            this.notification.handleError(error, "Failed to complete breakdown without work report.");
          }
        });
  }

  public getOtherMainCallcentre(incident: Incident): IncidentClientLink | undefined {
    return incident.incidentClients.filter(link => link.connectionTypeId === 2 && link.isActive && link.clientId !== this.authService.user?.clientId)[0];
  }

  
}
